import {
  colorBaseNeutralAlt800,
  colorBrandSecondaryBoldest,
  colorBrandSecondarySoftest,
  colorContentDefault,
  colorContentDefaultInverse,
  colorContentDim,
  colorOutlineSoft,
} from '@duxton-web/design-tokens/themes/light';
import {
  ArrowLeftUniversal,
  BatteryFullUniversal,
  SignalAlt100Universal,
  TrashCanOutlined,
  WifiUniversal,
} from '@duxton-web/icons';
import { Button, Flex, Form, Tag, Typography } from '@duxton-web/ui';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';

import {
  SMART_CAMPAIGN_FORM_KEYS,
  SMART_CAMPAIGN_INBOX_CATEGORIES,
} from 'omega-rtc/constants/ucd';

const SmartCampaignPCIPreviewCard = ({
  imageURL,
  language,
  handleRemoveImage,
}) => {
  const form = Form.useFormInstance();
  const watchTitle = Form.useWatch([SMART_CAMPAIGN_FORM_KEYS.PCI_TITLE], form);
  const watchMessage = Form.useWatch(
    [SMART_CAMPAIGN_FORM_KEYS.PCI_MESSAGE],
    form,
  );
  const watchCategory = Form.useWatch(
    SMART_CAMPAIGN_FORM_KEYS.PCI_CATEGORY,
    form,
  );

  const chosenCategory = useMemo(() => {
    return SMART_CAMPAIGN_INBOX_CATEGORIES.find(
      (CATEGORY) => CATEGORY.value === watchCategory,
    );
  }, [watchCategory]);

  const watchButtonText = Form.useWatch(
    [SMART_CAMPAIGN_FORM_KEYS.PCI_BUTTON_TEXT],
    form,
  );
  const title = useMemo(
    () => (watchTitle && watchTitle[language]) || '',
    [watchTitle, language],
  );

  const message = useMemo(
    () => (watchMessage && watchMessage[language]) || '',
    [watchMessage, language],
  );

  const buttonText = useMemo(
    () => (watchButtonText && watchButtonText[language]) || '',
    [watchButtonText, language],
  );

  return (
    <Flex
      vertical
      style={{
        width: 377,
        borderRadius: '16px',
        height: '732px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: colorOutlineSoft,
        position: 'relative', // Add relative positioning to the parent
      }}
    >
      <Flex
        vertical
        style={{
          ...(imageURL && {
            height: 176,
            // marginTop and marginLeft is set to '-1px' to cover border of parent div.
            marginTop: '-1px',
            marginLeft: '-1px',
          }),
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundImage: imageURL ? `url(${imageURL})` : 'none',
        }}
      >
        <Flex
          vertical={false}
          justify="space-between"
          style={{ padding: '12px 15px' }}
        >
          <Typography.Body level={4}>{dayjs().format('HH:mm')}</Typography.Body>
          <Flex vertical={false} gap={2}>
            <WifiUniversal style={{ fontSize: 18 }} />
            <SignalAlt100Universal style={{ fontSize: 18 }} />
            <BatteryFullUniversal style={{ fontSize: 18 }} />
          </Flex>
        </Flex>
        <Flex
          vertical={false}
          justify="space-between"
          gap={8}
          style={{ padding: '12px 16px' }}
        >
          <PreviewButton
            Icon={ArrowLeftUniversal}
            isButtonActive={imageURL}
            handleClick={(e) => e.preventDefault()}
          />
          <PreviewButton
            Icon={TrashCanOutlined}
            isButtonActive={imageURL}
            handleClick={(e) => {
              handleRemoveImage();
              e.preventDefault();
            }}
          />
        </Flex>
      </Flex>
      <Flex
        vertical
        style={{
          padding: '16px',
          height: '550px',
          margin: '0px 8px',
          color: colorContentDim,
        }}
        gap={16}
      >
        <Flex
          vertical
          gap={16}
          style={{
            overflowY: 'auto', // Enable vertical scrolling
            flexGrow: 1, // Allow the content to grow and take available space
            scrollbarWidth: 'thin',
          }}
        >
          {chosenCategory && (
            <Tag
              size="xsmall"
              icon={chosenCategory.icon}
              style={{
                color: chosenCategory.color || colorBrandSecondaryBoldest,
                backgroundColor:
                  chosenCategory.backgroundColor || colorBrandSecondarySoftest,
                width: 'fit-content',
                border: 0,
                borderRadius: '16px',
                padding: '4px 8px',
              }}
            >
              {chosenCategory.previewDisplay || watchCategory}
            </Tag>
          )}
          <Flex vertical gap={8}>
            <Typography.Body
              level={1}
              style={{
                color: title ? colorBaseNeutralAlt800 : colorContentDim,
                whiteSpace: 'pre-wrap',
              }}
            >
              {title || 'Title'}
            </Typography.Body>
            <Typography.Metadata level={1}>
              Message sent time
            </Typography.Metadata>
          </Flex>
          <div
            style={{
              flexWrap: 'wrap',
              lineHeight: '24px',
              wordBreak: 'break-word',
              paddingBottom: 101,
              color: message ? colorBaseNeutralAlt800 : colorContentDim,
            }}
            dangerouslySetInnerHTML={{
              __html: message || 'Message',
            }}
          />
        </Flex>
      </Flex>
      <Flex
        vertical
        gap={16}
        style={{
          backgroundImage:
            'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))',
          borderRadius: '16px',
          position: 'absolute', // Fix to the bottom of the parent
          bottom: 0, // Align to the bottom
          flexGrow: 1, // Allow the content to grow and take available space
          width: '100%', // Take the full width of the parent
          padding: '16px', // Add padding to match the rest of the content
        }}
      >
        {buttonText && (
          <Button
            size="large"
            type="primary"
            style={{ justifyContent: 'center', whiteSpace: 'pre-wrap' }}
          >
            {buttonText || 'Button text'}
          </Button>
        )}
        <Flex
          vertical={false}
          style={{
            alignSelf: 'center',
          }}
        >
          <span
            style={{
              width: '134px',
              height: '5px',
              borderRadius: '100px',
              backgroundColor: colorContentDefault,
            }}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

const PreviewButton = ({ handleClick, isButtonActive, Icon }) => {
  return (
    <button
      onClick={handleClick}
      style={{
        width: '40px',
        height: '40px',
        border: 'none',
        cursor: 'pointer',
        backgroundColor: 'transparent',
      }}
    >
      <Icon
        style={{
          fontSize: '24px',
          color: isButtonActive
            ? colorContentDefaultInverse
            : colorContentDefault,
        }}
      />
    </button>
  );
};

export default SmartCampaignPCIPreviewCard;
